<template>
    <Dialog
        :visible="visible"
        @update:visible="updateVisible"
        header="Upload IA (Beta)"
        @show="onShow"
        @hide="onHide"
        class="w-11 lg:w-5"
        modal
    >
        <form @submit.prevent="onSubmit">
            <div class="w-full">

                <Fieldset legend="Contrato">
                    <p class="text-sm text-gray-400 mb-3">
                        <i class="pi pi-info-circle mr-1"></i>
                        Selecione o contrato ao qual esta conta será vinculada (igual ao upload padrão).
                    </p>
                    <div class="field mt-2">
                        <InputGroup>
                            <FloatLabel>
                                <InputText id="upload_ia_contrato" v-model="id_primario" class="w-full" readonly placeholder=" " />
                                <label for="upload_ia_contrato">Contrato<span class="text-red-500"> *</span></label>
                            </FloatLabel>
                            <Button icon="pi pi-search" severity="info" type="button" @click="visibleContratoListSelect = true" />
                            <Button icon="pi pi-times" severity="danger" type="button" @click="clearContrato" />
                        </InputGroup>
                    </div>
                    <Message
                        v-if="pagamentoRequisitos.msg && !pagamentoRequisitos.ok"
                        severity="warn"
                        :closable="false"
                        class="mt-2 w-full"
                    >
                        {{ pagamentoRequisitos.msg }}
                    </Message>
                </Fieldset>

                <!-- Upload de Arquivo -->
                <Fieldset legend="Upload de Documento" class="mt-3">
                    <p class="text-sm text-gray-400 mb-3">
                        <i class="pi pi-info-circle mr-1"></i>
                        Envie uma foto, print ou PDF do boleto. A IA irá extrair os dados automaticamente.
                    </p>

                    <BlockUI :blocked="isLoading" class="relative w-full">
                        <Toolbar>
                            <template #end>
                                <div class="flex gap-1">
                                    <Button
                                        label="Selecionar Arquivo"
                                        icon="pi pi-folder-open"
                                        severity="primary"
                                        type="button"
                                        @click="openFileDialog"
                                    />
                                    <Button
                                        label="Limpar"
                                        icon="pi pi-trash"
                                        severity="danger"
                                        type="button"
                                        @click="clearFileDialog"
                                    />
                                </div>
                            </template>
                        </Toolbar>

                        <div class="field mt-4">
                            <FilePond
                                ref="pond"
                                name="arquivo"
                                labelIdle='Arraste o arquivo ou <span class="filepond--label-action">clique aqui</span>'
                                :acceptedFileTypes="['image/*', 'application/pdf']"
                                :files="files"
                                @updatefiles="onFileUpdate"
                                :allowMultiple="false"
                                :instantUpload="false"
                            />
                        </div>

                        <div v-if="isLoading" class="absolute" style="top: calc(50% - 20px); left: calc(50% - 20px);">
                            <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="6" />
                        </div>
                    </BlockUI>
                </Fieldset>

            </div>

            <!-- Botões -->
            <div class="flex justify-content-end gap-2 mt-4">
                <Button
                    label="Analisar com IA"
                    icon="pi pi-sparkles"
                    severity="help"
                    type="button"
                    :loading="isLoading"
                    :disabled="!podeAnalisarPagamento"
                    @click="handleAnalyzeIA"
                />
                <Button
                    label="Fechar"
                    icon="pi pi-times"
                    severity="warn"
                    type="button"
                    @click="updateVisible(false)"
                />
            </div>
        </form>

        <ContratoListSelect
            :visible="visibleContratoListSelect"
            :permissoes="permissoes"
            @close="visibleContratoListSelect = false"
            @select="selectContrato"
        />

        <!-- Beneficiário divergente: em vez de só confirmar, deixa corrigir nome/CNPJ e salvar na conta. -->
        <Dialog
            :visible="benefDialog.visible"
            @update:visible="v => { if (!v) cancelBeneficiario() }"
            modal
            header="Atenção — confira o beneficiário"
            class="w-11 lg:w-5"
        >
            <Message severity="warn" :closable="false" class="mb-4 w-full">
                {{ benefDialog.aviso }}
            </Message>
            <p class="text-sm text-gray-400 mb-5">
                <i class="pi pi-info-circle mr-1"></i>
                Corrija se necessário e cadastre. O beneficiário fica vinculado ao provedor deste contrato,
                vai para a remessa (J-52) e não gera aviso nas próximas.
            </p>

            <div class="flex flex-column gap-5 pt-2">
                <FloatLabel>
                    <InputText id="benef_nome" v-model="benefDialog.nome" class="w-full" />
                    <label for="benef_nome">Beneficiário (nome)</label>
                </FloatLabel>
                <div>
                    <FloatLabel>
                        <InputText id="benef_cnpj" v-model="benefDialog.cnpj" class="w-full" />
                        <label for="benef_cnpj">CNPJ do beneficiário</label>
                    </FloatLabel>
                    <small v-if="benefCnpjInvalido" class="text-red-500 block mt-1">
                        Informe um CNPJ com 14 dígitos ou deixe em branco.
                    </small>
                </div>
            </div>

            <template #footer>
                <Button label="Cancelar" icon="pi pi-times" severity="secondary" text type="button" :disabled="benefDialog.saving" @click="cancelBeneficiario" />
                <Button label="Cadastrar beneficiário" icon="pi pi-check" severity="warning" type="button" :loading="benefDialog.saving" :disabled="benefCnpjInvalido" @click="confirmBeneficiario" />
            </template>
        </Dialog>
    </Dialog>
</template>

<script>
import vueFilePond from 'vue-filepond'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import Fieldset from 'primevue/fieldset';
import Toolbar from 'primevue/toolbar';
import BlockUI from 'primevue/blockui';
import ProgressSpinner from 'primevue/progressspinner';
import FloatLabel from 'primevue/floatlabel';
import InputText from 'primevue/inputtext';
import InputGroup from 'primevue/inputgroup';
import Message from 'primevue/message';

import 'filepond/dist/filepond.min.css'

import Dialog from 'primevue/dialog'
import Button from 'primevue/button'

import ContratoListSelect from '@/page/contrato/ListaSelect.vue';

import Conta from '@/service/conta.js'
import Provedor from '@/service/provedor.js'
import Mask from '@/utils/Mask.js'

const FilePond = vueFilePond(FilePondPluginFileValidateType)

export default {
    components: {
        Dialog,
        Button,
        Fieldset,
        FilePond,
        Toolbar,
        ProgressSpinner,
        BlockUI,
        FloatLabel,
        InputText,
        InputGroup,
        ContratoListSelect,
    },
    props: {
        visible: Boolean,
        permissoes: {
            type: Object,
            default: () => ({}),
        },
    },
    emits: ['close', 'success', 'duplicate-contas'],
    data() {
        return {
            files: [],
            isLoading: false,
            analyzeRequestId: 0,
            contrato_id: null,
            id_primario: null,
            contrato: {},
            visibleContratoListSelect: false,

            pagamentoRequisitos: { ok: true, msg: null, erros: {} },

            benefDialog: { visible: false, aviso: '', nome: '', cnpj: '', payload: null, saving: false },
        }
    },
    computed: {
        podeAnalisarPagamento() {
            return this.contrato_id && this.pagamentoRequisitos.ok !== false;
        },
        benefCnpjInvalido() {
            const d = (this.benefDialog.cnpj || '').replace(/\D/g, '');
            return d.length > 0 && d.length !== 14;
        },
    },
    methods: {
        loadPagamentoRequisitos() {
            if (!this.contrato_id) {
                this.pagamentoRequisitos = { ok: true, msg: null, erros: {} };
                return;
            }

            Conta.pagamentoRequisitos({ contrato_id: this.contrato_id })
                .then((response) => {
                    this.pagamentoRequisitos = {
                        ok: response.data.ok !== false,
                        msg: response.data.msg || null,
                        erros: response.data.erros || {},
                    };
                })
                .catch((error) => {
                    const payload = error.response?.data || {};
                    this.pagamentoRequisitos = {
                        ok: false,
                        msg: payload.msg || 'Contrato não apto para análise de pagamento.',
                        erros: payload.erros || {},
                    };
                });
        },
        selectContrato(contrato) {
            if (!contrato || !contrato.id) {
                this.clearContrato();
                return;
            }
            this.contrato = contrato;
            this.contrato_id = contrato.id;
            this.id_primario = contrato.id_primario ?? null;
            this.loadPagamentoRequisitos();
        },
        clearContrato() {
            this.contrato = {};
            this.contrato_id = null;
            this.id_primario = null;
            this.pagamentoRequisitos = { ok: true, msg: null, erros: {} };
        },
        onFileUpdate(files) {
            this.files = files.map(file => file.file)
        },
        openFileDialog() {
            this.$refs.pond.browse()
        },
        clearFileDialog() {
            this.files = []
        },
        isRequestActive(requestId) {
            return this.visible && requestId === this.analyzeRequestId
        },

        /**
         * API pode retornar msg (ValidationResponse), message (Laravel padrão) ou erros/erros em objeto.
         */
        formatAnalyzeIAError(error) {
            const d = error.response?.data
            if (d == null) {
                return error.message || 'Sem resposta do servidor. Verifique rede e tamanho do arquivo (PHP post_max_size).'
            }
            if (typeof d === 'string') {
                return 'Resposta inválida do servidor (HTML ou texto). Confira Accept: application/json no cliente.'
            }
            if (typeof d.msg === 'string' && d.msg) {
                return d.msg
            }
            if (typeof d.message === 'string' && d.message) {
                return d.message
            }
            const bag = d.erros || d.errors
            if (bag && typeof bag === 'object') {
                const first = Object.values(bag).flat()[0]
                if (first) {
                    return Array.isArray(first) ? first[0] : first
                }
            }
            return 'Falha ao processar arquivo com a IA. Tente o upload padrão.'
        },

        async handleAnalyzeIA() {
            if (!this.contrato_id) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Atenção',
                    detail: 'Selecione um contrato antes de analisar.',
                    life: 5000
                })
                return
            }
            if (!this.podeAnalisarPagamento) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Análise bloqueada',
                    detail: this.pagamentoRequisitos.msg || 'O contrato não atende aos requisitos de pagamento.',
                    life: 8000,
                });
                return;
            }
            if (this.files.length === 0) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Atenção',
                    detail: 'Nenhum arquivo selecionado.',
                    life: 5000
                })
                return
            }

            this.isLoading = true
            const requestId = this.analyzeRequestId + 1
            this.analyzeRequestId = requestId
            const contratoId = this.contrato_id
            const idPrimario = this.id_primario

            const reader = new FileReader()
            reader.onload = async () => {
                try {
                    if (!this.isRequestActive(requestId)) {
                        return
                    }
                    const base64String = reader.result
                    const response = await Conta.analyzeIA({
                        file: base64String,
                        contrato_id: contratoId,
                    })
                    if (!this.isRequestActive(requestId)) {
                        return
                    }

                    const d = response.data

                    if (
                        d.duplicado
                        && Array.isArray(d.contas)
                        && d.contas.length > 0
                    ) {
                        this.$emit('duplicate-contas', {
                            msg:
                                d.msg
                                || 'Existem um ou mais códigos de barras já cadastrados neste arquivo.',
                            contas: d.contas,
                        })
                        return
                    }

                    const payload = {
                        ...d,
                        contrato_id: contratoId,
                        id_primario: idPrimario,
                    }

                    if (d.aviso_fatura_agrupada) {
                        this.$toast.add({
                            severity: 'info',
                            summary: 'Fatura agrupada',
                            detail: `${d.aviso_fatura_agrupada} Lance só o valor deste circuito.`,
                            life: 15000
                        })
                    }

                    const avisos = Array.isArray(d.avisos) ? d.avisos : []
                    if (avisos.length > 0) {
                        this.benefDialog = {
                            visible: true,
                            aviso: avisos.join(' '),
                            nome: d.beneficiario || '',
                            cnpj: d.beneficiario_cnpj ? Mask.cnpj(d.beneficiario_cnpj) : '',
                            payload,
                        }
                        return
                    }

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Sucesso',
                        detail: 'Dados extraídos com sucesso pela IA.',
                        life: 3000
                    })

                    this.$emit('success', payload)
                } catch (error) {
                    if (!this.isRequestActive(requestId)) {
                        return
                    }
                    console.error('Erro na análise via IA:', error)
                    const payload = error.response?.data
                    if (payload !== undefined) {
                        console.error('Resposta da API (analyze-ia):', payload)
                    }
                    const detail = this.formatAnalyzeIAError(error)
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail,
                        life: 8000
                    })
                } finally {
                    if (requestId === this.analyzeRequestId) {
                        this.isLoading = false
                    }
                }
            }
            reader.onerror = () => {
                if (!this.isRequestActive(requestId)) {
                    return
                }
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Falha ao ler o arquivo selecionado.',
                    life: 5000
                })
                if (requestId === this.analyzeRequestId) {
                    this.isLoading = false
                }
            }
            try {
                reader.readAsDataURL(this.files[0])
            } catch (error) {
                if (requestId === this.analyzeRequestId) {
                    this.isLoading = false
                }
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Não foi possível ler o arquivo selecionado neste dispositivo.',
                    life: 7000
                })
            }
        },

        /**
         * "Cadastrar beneficiário": grava nome + CNPJ no payload da conta e, quando há CNPJ+nome válidos,
         * cadastra o beneficiário vinculado ao provedor do contrato (pré-cadastro — silencia avisos futuros).
         * Sem CNPJ válido (ex.: aviso de valor/vencimento), apenas segue salvando a conta.
         */
        async confirmBeneficiario() {
            if (this.benefCnpjInvalido) {
                return
            }
            const nome = (this.benefDialog.nome || '').trim()
            const cnpj = (this.benefDialog.cnpj || '').replace(/\D/g, '')
            const payload = {
                ...this.benefDialog.payload,
                beneficiario: nome || null,
                beneficiario_cnpj: cnpj || null,
            }

            if (cnpj.length === 14 && nome) {
                this.benefDialog.saving = true
                try {
                    await Provedor.beneficiarioSalvar({
                        contrato_id: this.benefDialog.payload?.contrato_id,
                        cnpj,
                        nome,
                    })
                } catch (error) {
                    this.benefDialog.saving = false
                    const msg = error.response?.data?.msg || 'Falha ao cadastrar o beneficiário.'
                    this.$toast.add({ severity: 'error', summary: 'Erro', detail: msg, life: 7000 })
                    return
                }
                this.benefDialog.saving = false
            }

            this.benefDialog.visible = false
            this.$emit('success', payload)
        },
        cancelBeneficiario() {
            if (this.benefDialog.saving) {
                return
            }
            this.benefDialog.visible = false
        },

        updateVisible(visible) {
            this.$emit('close', visible)
        },

        onShow() {
            //
        },
        onHide() {
            this.analyzeRequestId += 1
            this.isLoading = false
            this.files = []
            this.clearContrato()
            this.benefDialog = { visible: false, aviso: '', nome: '', cnpj: '', payload: null, saving: false }
        }
    }
}
</script>

<style scoped>
.field {
    margin-bottom: 1rem;
}
</style>
